/**
 * Tipos da linguagem de condição de formulários (contrato da T8a).
 *
 * Núcleo puro: nada aqui importa React, axios ou o barrel `_metronic/helpers`. É o que permite
 * rodar a fixture de paridade no Jest sem carregar a aplicação inteira.
 *
 * O JSON gravado usa sempre o token ASCII do operador; os símbolos ≠ ≥ ≤ existem só como rótulo de
 * tela, vindos de `operadores[].simbolo` do catálogo.
 */

/** Operadores aceitos. `existe`/`nao_existe` não levam a chave `valor`. */
export type Operador =
  | '='
  | '!='
  | '>'
  | '>='
  | '<'
  | '<='
  | 'entre'
  | 'em'
  | 'existe'
  | 'nao_existe'

export const OPERADORES: readonly Operador[] = [
  '=',
  '!=',
  '>',
  '>=',
  '<',
  '<=',
  'entre',
  'em',
  'existe',
  'nao_existe',
]

/** Operadores sem editor de valor. */
export const OPERADORES_SEM_VALOR: readonly Operador[] = ['existe', 'nao_existe']

export const operadorExigeValor = (op: Operador): boolean => !OPERADORES_SEM_VALOR.includes(op)

/**
 * Origem de uma referência. A condição de **exibição** de um campo só aceita `campo` e `variavel`
 * (rodada 14); `sinal` existe no avaliador para manter paridade com a fixture da T8a, que também
 * cobre o uso REGRA do protocolo.
 */
export type Fonte = 'campo' | 'variavel' | 'sinal'

/** Lado direito da comparação, na forma em que é gravado no JSON. */
export type ValorCondicao = number | string | number[] | string[]

export type CondicaoAtomica = {
  fonte: Fonte
  ref: string
  op: Operador
  valor?: ValorCondicao
}

export type GrupoTodos = { todos: Condicao[] }
export type GrupoAlgum = { algum: Condicao[] }
export type Grupo = GrupoTodos | GrupoAlgum
export type Condicao = Grupo | CondicaoAtomica

export type TipoGrupo = 'todos' | 'algum'

export const ehGrupoTodos = (c: Condicao): c is GrupoTodos => Array.isArray((c as GrupoTodos).todos)

export const ehGrupoAlgum = (c: Condicao): c is GrupoAlgum => Array.isArray((c as GrupoAlgum).algum)

export const ehGrupo = (c: Condicao): c is Grupo => ehGrupoTodos(c) || ehGrupoAlgum(c)

export const tipoDoGrupo = (grupo: Grupo): TipoGrupo => (ehGrupoTodos(grupo) ? 'todos' : 'algum')

export const itensDoGrupo = (grupo: Grupo): Condicao[] =>
  ehGrupoTodos(grupo) ? grupo.todos : (grupo as GrupoAlgum).algum

export const montarGrupo = (tipo: TipoGrupo, itens: Condicao[]): Grupo =>
  tipo === 'todos' ? { todos: itens } : { algum: itens }

/** Profundidade máxima de grupos aninhados aceita pelo backend. */
export const PROFUNDIDADE_MAXIMA = 5

/** Tipo de campo do ponto de vista da linguagem de condição (espelha `TipoCampoCondicao`). */
export type TipoCampoCondicao = 'NUMERO' | 'TEXTO' | 'OPCAO' | 'NAO_SUPORTADO'

/** Tipo semântico de uma referência (espelha `TipoValor`). */
export type TipoValor = 'NUMERO' | 'TEXTO' | 'LISTA'

export const tipoValorDoCampo = (tipo: TipoCampoCondicao): TipoValor | null => {
  switch (tipo) {
    case 'NUMERO':
      return 'NUMERO'
    case 'TEXTO':
    case 'OPCAO':
      return 'TEXTO'
    default:
      return null
  }
}

/** Descrição mínima de um campo para validar e montar condições (espelha `CampoEsquema`). */
export type CampoEsquema = {
  codigo: string
  tipo: TipoCampoCondicao
  multiplo: boolean
  ordem: number
  /** Desempate de campos com a mesma `ordem` (id do campo ou índice na lista). */
  desempate: number
  codigosOpcao: string[]
}

/** `true` quando `campo` vem antes de `outro` na ordem de preenchimento. */
export const antesDe = (campo: CampoEsquema, outro: CampoEsquema): boolean =>
  campo.ordem !== outro.ordem ? campo.ordem < outro.ordem : campo.desempate < outro.desempate

/** Valor observado no contexto de avaliação. */
export type ValorContexto =
  | { tipo: 'numero'; v: number }
  | { tipo: 'texto'; v: string }
  | { tipo: 'textos'; v: string[] }

export type ContextoCondicao = {
  /** Respostas do formulário, por código de campo. */
  respostas: Record<string, ValorContexto>
  /** Variáveis do atendimento já resolvidas, por código do catálogo. */
  variaveis: Record<string, ValorContexto>
  /** Sinais vitais aferidos, por código. Só o uso REGRA (protocolo) referencia sinal. */
  sinais: Record<string, number>
}

export const contextoVazio = (): ContextoCondicao => ({
  respostas: {},
  variaveis: {},
  sinais: {},
})

/** Códigos de erro estruturais devolvidos pela validação (espelha `CodigoErroCondicao`). */
export type CodigoErroCondicao =
  | 'JSON_INVALIDO'
  | 'ESTRUTURA_INVALIDA'
  | 'GRUPO_VAZIO'
  | 'PROFUNDIDADE_EXCEDIDA'
  | 'FONTE_DESCONHECIDA'
  | 'FONTE_NAO_PERMITIDA'
  | 'OPERADOR_DESCONHECIDO'
  | 'CAMPO_INEXISTENTE'
  | 'CAMPO_POSTERIOR'
  | 'CAMPO_TIPO_NAO_SUPORTADO'
  | 'VARIAVEL_DESCONHECIDA'
  | 'SINAL_DESCONHECIDO'
  | 'OPERADOR_INCOMPATIVEL'
  | 'TIPO_VALOR_INCOMPATIVEL'
  | 'VALOR_FORA_DO_DOMINIO'
  | 'INTERVALO_INVALIDO'

/** Erro de validação, com o mesmo `caminho` (`$.todos[1].algum[0].op`) que o backend devolve. */
export type ErroCondicao = {
  caminho: string
  codigo: CodigoErroCondicao
  mensagem: string
}

// Nomes usados pelo tradutor de condição e pelas telas de protocolo (triagem).
export type OperadorCondicao = Operador
export type FonteCondicao = Fonte
export type GrupoCondicao = Grupo
export const isGrupoTodos = ehGrupoTodos
export const isGrupoAlgum = ehGrupoAlgum
export const isGrupo = ehGrupo
